import type { ColumnsType } from 'antd/es/table'
import type { SortOrder } from 'antd/es/table/interface'
import type { MouseEvent } from 'react'

import { OrderType } from 'src/types'

type SortState = {
  columnKey: string
  order: SortOrder
}

const stopPropagationHandlers = {
  onClick: (e: MouseEvent<HTMLElement>) => e.stopPropagation(),
  onMouseDown: (e: MouseEvent<HTMLElement>) => e.stopPropagation(),
  onPointerDown: (e: MouseEvent<HTMLElement>) => e.stopPropagation()
}

const actionKeys = ['actions', 'action']

const SINGLE_COLUMN_SORTER = { multiple: undefined }

export const isActionColumn = <T>(col: ColumnsType<T>[number]): boolean =>
  'key' in col && actionKeys.includes(col.key as string)

export const hasChildrenColumns = <T>(
  col: ColumnsType<T>[number]
): col is ColumnsType<T>[number] & { children: ColumnsType<T> } =>
  'children' in col && Array.isArray(col.children)

const getColumnSortKey = <T>(
  col: ColumnsType<T>[number]
): string | undefined => {
  if ('key' in col && col.key !== undefined && col.key !== null) {
    return String(col.key)
  }

  return undefined
}

export const enhanceColumns = <T>(
  cols: ColumnsType<T>,
  sortStates: SortState[]
): ColumnsType<T> =>
  cols?.map((col) => {
    const isActionsColumn = isActionColumn(col)
    const isNestedColumn = hasChildrenColumns(col)
    const columnKey = getColumnSortKey(col)
    const sortState = sortStates?.find((state) => state.columnKey === columnKey)

    return {
      ...col,
      ...(isNestedColumn
        ? { children: enhanceColumns(col.children, sortStates) }
        : {
            ...('sorter' in col && col.sorter === true
              ? { sorter: SINGLE_COLUMN_SORTER }
              : {}),
            sortOrder: sortState?.order ?? null,
            defaultSortOrder: sortState?.order
          }),
      ...(isActionsColumn && { onCell: () => stopPropagationHandlers })
    }
  })

export const getSortStateFromOrdering = (
  ordering?: string | Array<string> | null
): SortState[] =>
  (Array.isArray(ordering) ? ordering : [ordering])
    .flatMap((term) => String(term ?? '').split(','))
    .map((term) => term.trim())
    .filter(Boolean)
    .map((term) => {
      const isDesc = term.startsWith('-')

      return {
        columnKey: isDesc ? term.slice(1) : term,
        order: isDesc ? OrderType.DESCEND : OrderType.ASCEND
      }
    })
